import React, { FocusEvent, useCallback } from 'react';
import { Field, FieldInputProps, FieldMetaState, useFormState } from 'react-final-form';

import { MDText } from 'i18n-react';

import { Input, InputPatternFormat } from '@motorway/mw-highway-code/alpha';
import { SIZE } from '@motorway/mw-highway-code/enums';

import { isEmptyObject } from 'Utilities/helpers';

import { InputLabel } from 'Components/inputLabel/InputLabel';
import LocalTexts from 'Containers/app/documentCapture/DocumentCapture.texts.json';

import styles from './AddBankDetailsField.scss';

const LocalT = new MDText(LocalTexts.addBankDetails.fields);

export type Intent = 'success'| 'error'| 'warning' | null;

type AddBankDetailsFieldProps = {
  feedbackTooltip?: {
    content?: React.ReactNode;
  };
  inputProps?: {
    format?: string;
    mask?: string;
    thousandSeparator?: boolean;
  };
  intent?: Intent;
  meta?: FieldMetaState<string>;
  name: string;
  onInputFocus?: () => void;
  validate?: (value: string) => string;
}
type SharedInputProps = { id: string; input: FieldInputProps<string, HTMLElement>; meta: FieldMetaState<string>}

export const AddBankDetailsField = ({
  inputProps = {},
  intent,
  name,
  onInputFocus = () => {},
  validate = () => '',
}: AddBankDetailsFieldProps) => {
  const { submitting: disabled } = useFormState();

  const sharedFormProps = useCallback(
    (input: FieldInputProps<string, HTMLElement>) => ({
      input: {
        ...input,
        autoComplete: 'off',
        autoCorrect: 'off',
        disabled,
        onFocus: (e?: FocusEvent<HTMLElement, Element>) => {
          input.onFocus(e);
          onInputFocus?.();
        },
        placeholder: LocalT.translate(`${name}.placeholder`),
      },
    }),
    [disabled, name, onInputFocus],
  );

  const validateMessage = ({ meta }: Pick<AddBankDetailsFieldProps, 'meta'>) => intent && meta?.error;

  const sharedInputProps = ({ id, input, meta }: SharedInputProps) => ({
    formProps: sharedFormProps(input),
    fullWidth: true,
    id,
    intent: intent ?? undefined,
    label: (
      <InputLabel
        labelText={LocalT.translate(`${name}.label`)}
        tooltip={LocalT.translate(`${name}.hint`)}
      />
    ),
    showLabel: true,
    size: SIZE.MEDIUM,
    validationMessage: validateMessage({ meta }),
  });

  return (
    <Field {...{ name, validate }} id={`${name}-input`}>
      {({ id, input, meta }) => (
        <div className={styles.inputField}>
          {!isEmptyObject(inputProps)
            ? <InputPatternFormat
              format={inputProps.format as string}
              mask={inputProps.mask}
              {...sharedInputProps({ id, input, meta })}
            />
            : <Input
              {...sharedInputProps({ id, input, meta })}
            />}
        </div>
      )}
    </Field>
  );
};
